"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { attributeDefinition, attributeValueSettings } from "@/lib/categories/attribute-definition";
import type { AttributeSchema } from "@/services/category.service";
import {
  CategoryService,
  type DescriptionTemplateInfo,
  type ResolvedAttributes,
  type TitlePlaceholders,
  type TitleTemplateInfo,
} from "@/services/category.service";
import type { LanguageCode } from "@/constants/languages";
import {
  attributeInheritanceState,
  seedAttributes,
  seedLabels,
} from "@/lib/categories/inheritance";
import { deriveAttributeKey } from "@/lib/categories/attribute-key";
import { localizedText } from "@/lib/categories/localization";
import { AttributeLabelsTable } from "./attribute-labels-table";
import { AttributeSection } from "./attribute-section";
import { DescriptionTemplateField } from "./description-template-field";
import { SchemaSaveBar } from "./schema-save-bar";
import { TitleTemplateField } from "./title-template-field";
import type {
  AttributeRow,
  CategoryDraft,
  DraftStore,
} from "./use-category-drafts";
import { emptyDraft, newRowUid, touchDraft } from "./use-category-drafts";

interface SchemaEditorProps {
  catalogueId: string;
  /** Category id, or the general pseudo-scope id. */
  categoryId: string;
  isGeneral: boolean;
  /** Name of the parent, for the inheritance sentences. Null for roots/general. */
  parentName: string | null;
  languages: LanguageCode[];
  readOnly: boolean;
  drafts: DraftStore;
  /** Descendants of the selected category, for the bulk scope control. */
  subcategoryIds: string[];
  onDraftsChanged: () => void;
  onSaved: () => void;
}

const toRows = (
  attributes: AttributeSchema,
  labels: Record<string, Record<string, string>>,
  primaryLanguage: string | undefined,
): AttributeRow[] =>
  Object.entries(attributes).map(([key, definition]) => ({
    uid: newRowUid(),
    key,
    label: (primaryLanguage ? labels[key]?.[primaryLanguage] : undefined) ?? "",
    ...attributeValueSettings(definition),
    isNew: false,
  }));

export function descriptionTemplateForMode(input: {
  mode: "replace" | "extend";
  draftTemplate: string;
  ownTemplate: string | null | undefined;
  resolvedTemplate: string | null | undefined;
}): string {
  return input.mode === "extend"
    && !input.ownTemplate
    && input.draftTemplate === (input.resolvedTemplate ?? "")
    ? ""
    : input.draftTemplate;
}

export function SchemaEditor({
  catalogueId,
  categoryId,
  isGeneral,
  parentName,
  languages,
  readOnly,
  drafts,
  subcategoryIds,
  onDraftsChanged,
  onSaved,
}: SchemaEditorProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(
    null,
  );

  const [meta, setMeta] = useState<ResolvedAttributes | null>(null);
  const [titleInfo, setTitleInfo] = useState<TitleTemplateInfo | null>(null);
  const [descriptionInfo, setDescriptionInfo] =
    useState<DescriptionTemplateInfo | null>(null);
  const [placeholders, setPlaceholders] = useState<TitlePlaceholders | null>(null);
  const [draft, setDraft] = useState<CategoryDraft>(emptyDraft());
  const [applyToSubtree, setApplyToSubtree] = useState(false);
  const [confirmSubtree, setConfirmSubtree] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /** Bumped after a write so the editor reseeds from what was actually stored. */
  const [reloadKey, setReloadKey] = useState(0);
  /** A reseed must not flash the spinner or wipe the message it just set. */
  const silentReload = useRef(false);

  const reseed = useCallback(() => {
    silentReload.current = true;
    setReloadKey((k) => k + 1);
  }, []);

  const primaryLanguage = languages[0];
  const inheritanceState = attributeInheritanceState(meta);
  const hasOwnAttributes = inheritanceState === "own";
  const sourceName = (source: Record<string, string> | string | null | undefined) =>
    source === "general"
      ? "the catalogue-wide defaults"
      : typeof source === "string"
        ? source
        : source
          ? localizedText(source)
          : null;

  const inheritedSource = meta?.resolved?.inheritedFrom;
  const inheritedFrom = isGeneral
    ? null
    : inheritedSource === "general"
      ? "the default attributes"
      : inheritedSource
        ? localizedText(inheritedSource)
        : (parentName ?? "the default attributes");

  // Load, then either restore the category's draft or seed from the API.
  useEffect(() => {
    let active = true;
    const silent = silentReload.current;
    silentReload.current = false;
    if (!silent) {
      setLoading(true);
      setMessage(null);
    }
    setError(null);

    Promise.all([
      CategoryService.getAttributes(catalogueId, categoryId),
      CategoryService.getTitleTemplate(catalogueId, categoryId),
      CategoryService.getTitlePlaceholders(catalogueId).catch(() => null),
      CategoryService.getDescriptionTemplate(catalogueId, categoryId),
    ])
      .then(([attrs, title, ph, description]) => {
        if (!active) return;
        setMeta(attrs);
        setTitleInfo(title);
        setPlaceholders(ph);
        setDescriptionInfo(description);

        const existing = drafts.get(categoryId);
        if (existing) {
          setDraft(existing);
          return;
        }
        const labels = seedLabels(attrs);
        setDraft({
          attributes: toRows(seedAttributes(attrs), labels, primaryLanguage),
          titleTemplate: title.own?.template ?? "",
          descriptionTemplate: description.own?.template ?? "",
          descriptionMode: description.own?.mode ?? "replace",
          descriptionTargetWordCount: description.own?.targetWordCount ?? null,
          labels,
          touched: new Set(),
        });
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => {
        if (active && !silent) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogueId, categoryId, drafts, primaryLanguage, reloadKey]);

  const commitDraft = useCallback(
    (next: CategoryDraft) => {
      setDraft(next);
      if (next.touched.size) drafts.set(categoryId, next);
      else drafts.clear(categoryId);
      onDraftsChanged();
    },
    [categoryId, drafts, onDraftsChanged],
  );

  const usedKeys = useMemo(() => {
    const template = draft.titleTemplate || titleInfo?.resolved || "";
    return new Set(
      (template.match(/\{(\w+)\}/g) ?? []).map((m) => m.slice(1, -1)),
    );
  }, [draft.titleTemplate, titleInfo]);

  const dirtyCount = draft.touched.size;

  const discard = () => {
    drafts.clear(categoryId);
    onDraftsChanged();
    setMessage(null);
    if (!meta) return;
    const labels = seedLabels(meta);
    setDraft({
      attributes: toRows(seedAttributes(meta), labels, primaryLanguage),
      titleTemplate: titleInfo?.own?.template ?? "",
      descriptionTemplate: descriptionInfo?.own?.template ?? "",
      descriptionMode: descriptionInfo?.own?.mode ?? "replace",
      descriptionTargetWordCount: descriptionInfo?.own?.targetWordCount ?? null,
      labels,
      touched: new Set(),
    });
  };

  const save = async (targetIds: string[] = [categoryId]) => {
    if (readOnly) return;
    setSaving(true);
    setMessage(null);
    setProgress(targetIds.length > 1 ? { done: 0, total: targetIds.length } : null);
    try {
      let queued = 0;

      if (draft.touched.has("attributes") || draft.touched.has("labels")) {
        const attributes: AttributeSchema = {};
        for (const row of draft.attributes) {
          const englishLabel = row.label.trim();
          if (!englishLabel) continue;
          const key = row.key || deriveAttributeKey(englishLabel);
          if (!key) continue;
          const labels = {
            ...(draft.labels[key] ?? {}),
            ...(primaryLanguage ? { [primaryLanguage]: englishLabel } : {}),
          };
          attributes[key] = attributeDefinition(row, labels, languages);
        }
        for (const [i, id] of targetIds.entries()) {
          const saved = await CategoryService.saveAttributes(catalogueId, id, {
            attributes,
            mode: "override",
          });
          queued += saved.reprocessing?.queued ?? 0;
          if (targetIds.length > 1) setProgress({ done: i + 1, total: targetIds.length });
        }
      }

      if (draft.touched.has("title")) {
        for (const id of targetIds) {
          const saved = await CategoryService.setTitleTemplate(
            catalogueId,
            id,
            draft.titleTemplate.trim() || null,
          );
          queued += saved.reprocessing?.queued ?? 0;
        }
      }

      if (draft.touched.has("description") || draft.touched.has("descriptionTarget")) {
        for (const id of targetIds) {
          const saved = await CategoryService.setDescriptionTemplate(
            catalogueId,
            id,
            {
              descriptionTemplate: draft.descriptionTemplate.trim() || null,
              descriptionMode: draft.descriptionMode,
              descriptionTargetWordCount: draft.descriptionTargetWordCount,
            },
          );
          queued += saved.reprocessing?.queued ?? 0;
        }
      }

      drafts.clear(categoryId);
      setApplyToSubtree(false);
      onDraftsChanged();
      reseed();
      const scope =
        targetIds.length > 1
          ? `Saved to this category and its ${targetIds.length - 1} subcategories.`
          : "Saved.";
      setMessage({
        type: "ok",
        text:
          queued > 0
            ? `${scope} Youzu is updating ${queued} product${queued === 1 ? "" : "s"}.`
            : `${scope} No products needed updating.`,
      });
      onSaved();
    } catch (e) {
      setMessage({ type: "err", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const resetChain = async (kind: "attributes" | "title" | "description") => {
    setSaving(true);
    try {
      if (kind === "attributes") {
        await CategoryService.saveAttributes(catalogueId, categoryId, {
          attributes: {},
          mode: "inherit",
        });
      } else if (kind === "title") {
        await CategoryService.setTitleTemplate(catalogueId, categoryId, null);
      } else {
        await CategoryService.setDescriptionTemplate(catalogueId, categoryId, {
          descriptionTemplate: null,
          descriptionMode: "replace",
          descriptionTargetWordCount: descriptionInfo?.own?.targetWordCount ?? null,
        });
      }
      drafts.clear(categoryId);
      onDraftsChanged();
      reseed();
      onSaved();
    } catch (e) {
      setMessage({ type: "err", text: e instanceof Error ? e.message : "Reset failed" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 pb-24 pt-5">
        <AttributeSection
          rows={draft.attributes}
          languages={languages}
          isGeneral={isGeneral}
          hasOwnAttributes={hasOwnAttributes}
          overriding={draft.touched.has("attributes")}
          inheritedFrom={inheritedFrom}
          readOnly={readOnly}
          usedKeys={usedKeys}
          onChange={(rows) =>
            commitDraft(touchDraft({ ...draft, attributes: rows }, "attributes"))
          }
          onCustomise={() => commitDraft(touchDraft(draft, "attributes"))}
          onReset={() => resetChain("attributes")}
        />

        <TitleTemplateField
          info={titleInfo}
          placeholders={placeholders ? {
            ...placeholders,
            placeholders: [...new Set([
              ...placeholders.placeholders,
              ...draft.attributes.map((attribute) => attribute.key).filter(Boolean),
            ])],
          } : null}
          value={draft.titleTemplate}
          hasOwn={!!draft.titleTemplate || draft.touched.has("title")}
          readOnly={readOnly}
          isCatalogueWide={isGeneral}
          inheritedFrom={sourceName(titleInfo?.inheritedFrom)}
          onChange={(v) =>
            commitDraft(touchDraft({ ...draft, titleTemplate: v }, "title"))
          }
          onCustomise={() =>
            commitDraft(
              touchDraft(
                { ...draft, titleTemplate: titleInfo?.resolved ?? "" },
                "title",
              ),
            )
          }
          onReset={() => resetChain("title")}
        />

        <DescriptionTemplateField
          info={descriptionInfo}
          value={draft.descriptionTemplate}
          hasOwn={!!draft.descriptionTemplate || draft.touched.has("description")}
          mode={draft.descriptionMode}
          targetWordCount={draft.descriptionTargetWordCount}
          hasOwnTarget={draft.descriptionTargetWordCount !== null}
          targetTouched={draft.touched.has("descriptionTarget")}
          readOnly={readOnly}
          isCatalogueWide={isGeneral}
          inheritedFrom={sourceName(descriptionInfo?.inheritedFrom)}
          onChange={(v) =>
            commitDraft(touchDraft({ ...draft, descriptionTemplate: v }, "description"))
          }
          onModeChange={(descriptionMode) =>
            commitDraft(
              touchDraft(
                {
                  ...draft,
                  descriptionMode,
                  descriptionTemplate: descriptionTemplateForMode({
                    mode: descriptionMode,
                    draftTemplate: draft.descriptionTemplate,
                    ownTemplate: descriptionInfo?.own?.template,
                    resolvedTemplate: descriptionInfo?.resolved,
                  }),
                },
                "description",
              ),
            )
          }
          onTargetWordCountChange={(descriptionTargetWordCount) =>
            commitDraft(
              touchDraft(
                { ...draft, descriptionTargetWordCount },
                "descriptionTarget",
              ),
            )
          }
          onCustomise={() =>
            commitDraft(
              touchDraft(
                { ...draft, descriptionTemplate: descriptionInfo?.resolved ?? "" },
                "description",
              ),
            )
          }
          onReset={() => resetChain("description")}
        />

        <AttributeLabelsTable
          rows={draft.attributes}
          languages={languages}
          labels={draft.labels}
          readOnly={readOnly}
          onChange={(labels) =>
            commitDraft(touchDraft({ ...draft, labels }, "labels"))
          }
        />

        {message && (
          <p
            className={
              message.type === "ok"
                ? "text-sm text-emerald-700"
                : "text-sm text-destructive"
            }
          >
            {message.text}{" "}
            {message.type === "ok" && (
              <Link
                className="font-medium underline"
                to={`/catalog/${catalogueId}?tab=products`}
              >
                View products
              </Link>
            )}
          </p>
        )}
      </div>

      <SchemaSaveBar
        dirtyCount={readOnly ? 0 : dirtyCount}
        saving={saving}
        subcategoryCount={subcategoryIds.length}
        applyToSubtree={applyToSubtree}
        confirmOpen={confirmSubtree}
        progress={progress}
        onApplyToSubtreeChange={setApplyToSubtree}
        onConfirmOpenChange={(open) => !open && setConfirmSubtree(false)}
        onDiscard={discard}
        onSave={() =>
          applyToSubtree && subcategoryIds.length
            ? setConfirmSubtree(true)
            : void save()
        }
        onConfirmSubtree={() => {
          setConfirmSubtree(false);
          void save([categoryId, ...subcategoryIds]);
        }}
      />

    </div>
  );
}
